import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import React, { useEffect, useId, useState } from 'react';
import styled, { css } from 'styled-components';
import { DeprecatedLinkButton as LinkButton, Placeholder, Text } from '@lmn/design-system';
import { InlineDatePicker } from '@/app/components/hotel-detail/InlineDatePicker';
import { lastMinuteDealsGradient } from '@/app/common/components/Promotions/styled';
import { RoomPanelPrice } from '@/app/components/hotel-detail/rooms/RoomPanelPrice';
import { PriceSticky } from '@/app/components/hotel-detail/PriceSticky';
import { useInView } from 'react-intersection-observer';
import { TextBase } from '@/app/components/hotel-detail/common/text';
import { PriceBreakdownModal, StyledExcludedChargesLabel } from '@/app/components/hotel-detail/rooms/LocalTaxes';
import { TaxesAllIncludedBreakdown } from '@/app/components/hotel-detail/rooms/TaxesAllIncludedBreakdown';
import { RoomMealPlanAndLuggageInformation } from '@/app/components/hotel-detail/rooms/RoomMealPlanAndLuggageInformation';
import {
  MealPlanDetailsDeferredPayments,
  MealPlanDetailsLoading,
} from '@/app/components/hotel-detail/rooms/RoomMealPlanInformation';
import { formatPrice } from '@/app/components/search/utils/formatting.ts';
import AddElementToLastWordInString from '@/app/common/components/Utility/AddElementToTheEndOfString';
import {
  hasPaymentInfo,
  isSplitPaymentsEligible,
} from '@/app/components/search/components/DeferredPayments/splitPayments.ts';
import usePriceDisplay from '@/app/components/hotel-detail/hooks/usePriceDisplay.ts';
import { useStickyHeaderOffset } from '@/app/components/hotel-detail/hooks/useStickyHeaderOffset.ts';
import { Icon } from '@/app/common/icons/Icon';
import { useIsMobile } from './hooks/useMediaSSR';
import { useTranslation } from '../translations/Translation';
import { useOffersContext } from './OffersProvider';
import { useUrlSearchParams } from './UrlSearchParamsProvider';
import useUserConfig from './hooks/useUserConfig';
import { getCheckinCheckoutDates, getFlightDates, SERVICES_DATES_FORMAT } from './common/dates';
import customCss from './css/customCss';
import { Title } from './HotelPageContentSection';
import { pageSectionIds } from './common/consts';
import { useFetchHotelPromotions, useHotelPromotion } from './hooks/useFetchHotelPromotions';
import LmnDealsExtras from './LmnDealsExtras';
import { PackageDetailsModal } from './rooms/PackageDetailsModal';
import { LmnDealHeader } from './LmnDealHeader';
import { getDoublePricingAmounts } from './rooms/roomCommon';

// #region styled components
const SectionContainer = styled.div`
  margin-top: 16px;
  @media ${customCss.breakpoints.sm} {
    padding: 0 24px;
  }

  @media ${customCss.breakpoints.lg} {
    padding: 0 24px;
  }

  @media ${customCss.breakpoints.lgPlus} {
    padding: 0 40px;
  }

  @media ${customCss.breakpoints.xl} {
    padding: 0;
  }
`;

export const PackageDetailsContainer = styled.div`
  position: relative;
  box-shadow: 0px 0px 4px 0px #00000005;
  box-shadow: 0px 2px 10px 0px #0000000a;
  box-shadow: 0px 10px 20px 0px #0000000a;

  @media ${customCss.breakpoints.sm} {
    margin-top: 0;
    border-radius: 8px 8px 0 0;
  }
`;

const DetailsInfo = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 2px;

  @media ${customCss.breakpoints.sm} {
    flex-direction: row;
    column-gap: 16px;
    flex-wrap: wrap;
  }
`;

const DetailInfoRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 2px 0;
`;

const RoomMealPlanInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media ${customCss.breakpoints.sm} {
    flex-direction: row;
    gap: 24px;
  }
`;
// #endregion

// ATENTION: This component CAN NOT use any hook that is getting branded fares like useBrandedFares()
// because it will cause a price discrepancy, this is previous step to get the transport data
export function PackageDetails(props: React.HTMLAttributes<HTMLDivElement>) {
  const { commonData, localization } = usePageWrapperData();
  const taxesIncludedPopup = localization?.configValuesApp?.taxesIncludedPopup || false;
  const taxesIncludedDoublePricing = localization?.configValuesApp?.taxesIncludedDoublePricing || false;
  const isMobile = useIsMobile(commonData.isMobileDevice);
  const { t } = useTranslation();
  const { isSoldout, offersData, isLoadingRooms } = useOffersContext();
  const { isTrain, accommodationOnly } = useUrlSearchParams();
  const { data: promotions } = useFetchHotelPromotions();
  const { showLmnDealsPromotion, showFlashSalesPromotion } = useHotelPromotion();
  const showNewLmnDealHeader = offersData?.hasFlashSaleOffers && showLmnDealsPromotion;
  const showPromotionLayout = showLmnDealsPromotion ? offersData?.hasFlashSaleOffers : showFlashSalesPromotion;
  const cheapestFilteredMealPlan = isLoadingRooms ? undefined : offersData?.cheapestFilteredMealPlan;

  // 100% at the bottom margin makes the intersection observer consider elements that are below the visible viewport as still "in view",
  // so the hook will only return false when the element scrolls above the top of the page (accounting for the fixed header offset,
  // which must match the `top` PriceSticky pins at).
  const { stickyTop } = useStickyHeaderOffset();
  const [ref, stickyRefInView] = useInView({
    threshold: 0,
    triggerOnce: false,
    rootMargin: `-${stickyTop}px 0px 100% 0px`,
    initialInView: true,
  });
  const fixedOnTop = !stickyRefInView;

  return (
    <>
      <SectionContainer {...props}>
        {isMobile && showPromotionLayout ? <LmnDealHeader expirationDate={promotions?.bookDate} /> : undefined}
        {!isMobile && (
          <Title>
            {accommodationOnly
              ? t('vacation.select.section.rooms.title')
              : isTrain
                ? t('vacation.select.train.summary.title')
                : t('vacation.select.summary.title')}
          </Title>
        )}
        <PackageDetailsContainer
          id={pageSectionIds.packageDetails}
          css={`
            background: ${
              showPromotionLayout
                ? showNewLmnDealHeader
                  ? lastMinuteDealsGradient
                  : customCss.gradients.flashSaleDeal
                : 'white'
            };
            ${
              isSoldout
                ? `padding: 16px; @media ${customCss.breakpoints.sm} { padding: 16px 24px; }`
                : showPromotionLayout
                  ? `padding: 0 4px 4px;`
                  : `padding: 0;`
            }

            @media ${customCss.breakpoints.sm} {
              padding: 0;
            }
          `}
        >
          {showPromotionLayout && !isMobile ? (
            <LmnDealHeader
              css={`
                border-radius: 8px 8px 0 0;
              `}
              expirationDate={promotions?.bookDate}
            />
          ) : undefined}
          <div
            css={`
              position: relative;
              display: flex;
              flex-direction: column;
              gap: 8px;
              background-color: white;
              padding: 16px;
              border-radius: 8px;

              @media ${customCss.breakpoints.sm} {
                flex-direction: row;
                gap: 16px;
                justify-content: space-between;
                ${showPromotionLayout ? 'border-radius: 0;' : undefined}
              }

              @media ${customCss.breakpoints.lg} {
                padding: 16px 24px;
              }
            `}
          >
            <div
              css={`
                display: flex;
                flex-direction: column;
                gap: 16px;
                background-color: white;

                @media ${customCss.breakpoints.sm} {
                  flex-direction: row;
                  justify-content: space-between;
                }
              `}
            >
              {isMobile && (
                <Title
                  css={`
                    padding: 0;
                  `}
                >
                  {accommodationOnly
                    ? t('vacation.select.details.tripoverview.title')
                    : isTrain
                      ? t('vacation.select.train.summary.title')
                      : t('vacation.select.summary.title')}
                </Title>
              )}
              <PackageDetailsList />
            </div>
            {isLoadingRooms ? (
              <div
                css={`
                  display: flex;
                  justify-content: flex-end;
                  align-items: flex-end;
                `}
              >
                <Placeholder height="24px" width="100px" />
              </div>
            ) : offersData?.rooms && !accommodationOnly ? (
              // On IOS we need to use another anchor element to know if the component is its sticky position, because the sticky element
              // is changing by small pixels his top position while scrolling.
              <div
                css={`
                  display: flex;
                  flex-direction: column;
                  align-items: flex-end;
                  justify-content: flex-end;
                `}
              >
                <RoomPanelPrice
                  ref={ref}
                  mealPlan={cheapestFilteredMealPlan}
                  roomsIncludedInPrice={offersData.rooms[0].roomsIncludedInPrice}
                  roomId={offersData.rooms[0].roomId}
                  hidePromotion={true}
                  hideLmnDeal={true}
                  hideTaxes={taxesIncludedPopup || taxesIncludedDoublePricing}
                  css={css`
                    justify-content: flex-end;
                    align-items: flex-end;
                    text-align: right;

                    .room-panel-price-anchor {
                      margin-bottom: 8px;
                    }

                    ${StyledExcludedChargesLabel} {
                      white-space: nowrap;
                    }
                  `}
                />
                {(taxesIncludedPopup || taxesIncludedDoublePricing) && cheapestFilteredMealPlan && (
                  <TaxesAllIncluded mealPlan={cheapestFilteredMealPlan} isDoublePricing={taxesIncludedDoublePricing} />
                )}
              </div>
            ) : null}
          </div>
        </PackageDetailsContainer>
      </SectionContainer>
      {!accommodationOnly ? <PriceSticky fixedOnTop={fixedOnTop} marginBottom={16} /> : undefined}
    </>
  );
}

export function TaxesAllIncluded({ mealPlan, isDoublePricing }: { mealPlan: MealPlanType; isDoublePricing: boolean }) {
  const [isModalOpen, setModalOpen] = useState(false);
  const { t } = useTranslation();
  const { localization } = usePageWrapperData();
  const { totalGuestsWithoutInfants } = useUrlSearchParams();
  const { isDoublePriceDP } = useUserConfig();
  const { showTotalPrice } = usePriceDisplay();
  const uid = useId();
  const randomId = `dp-taxes-all-included-${uid}`;

  if (!mealPlan.excludedCharges?.amount) return null;

  // Trigger label needs the same dpAmounts shape that TaxesAllIncludedBreakdown computes
  // internally — kept here for the "/per person" suffix on the visible link.
  const dpAmounts = getDoublePricingAmounts({
    pricePerPerson: mealPlan.priceWithoutTaxes || 0,
    taxesPerPerson: mealPlan.excludedCharges?.amount ?? 0,
    passengers: totalGuestsWithoutInfants,
    isDoublePriceDP,
    showTotalPrice,
  });

  const formattedAmount = isDoublePricing ? formatPrice({ price: dpAmounts.total, localization }) : undefined;
  const ppSuffix =
    isDoublePricing && dpAmounts.showPpSuffix ? ` ${t('vacation.select.room.price.dp.perperson.short')}` : '';

  const doublePricingLabel = isDoublePricing
    ? t('vacation.oss.cards.taxes_double_pricing.includes_label.short', {
        amount: `<strong>${formattedAmount}</strong>${ppSuffix}`,
      })
    : undefined;

  return (
    <>
      <PriceBreakdownModal
        isOpen={isModalOpen}
        id={randomId}
        onDismiss={() => setModalOpen(false)}
        onOpenByBrowserHistory={() => setModalOpen(true)}
        title={t('vacation.common.citytaxes.package.price.details.title')}
      >
        <TaxesAllIncludedBreakdown
          pricePerPerson={mealPlan.priceWithoutTaxes || 0}
          taxesPerPerson={mealPlan.excludedCharges?.amount ?? 0}
        />
      </PriceBreakdownModal>
      <div
        role="button"
        tabIndex={0}
        css={css`
          display: flex;
          align-items: center;
          gap: 4px;
          cursor: pointer;
          white-space: nowrap;
          ${(p) => p.theme.kallisto.mixins.font('Caption')};
          color: ${(p) => p.theme.kallisto.color.text.static.textSecondary};

          @media ${customCss.breakpoints.sm} {
            ${(p) => p.theme.kallisto.mixins.font('BodySmall')};
          }
        `}
        onClick={(e) => {
          e.stopPropagation();
          setModalOpen(true);
        }}
        onKeyUp={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.stopPropagation();
            setModalOpen(true);
          }
        }}
      >
        {/* Keep the info icon glued to the last word so it never wraps onto its
            own line when the label wraps (e.g. in the narrow EVO price column). */}
        <AddElementToLastWordInString
          text={
            isDoublePricing && doublePricingLabel
              ? doublePricingLabel
              : t('vacation.oss.cards.taxes_fees_included.all.title')
          }
        >
          <Icon
            category="Semantics"
            name="icon-circle-info"
            size={14}
            color="iconSecondary"
            alt=""
            css={`
              height: 14px;
              width: 14px;
              margin-left: 2px;
            `}
          />
        </AddElementToLastWordInString>
      </div>
    </>
  );
}

const PackageDetailsListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

function PackageDetailsList() {
  const { t } = useTranslation();
  const { data: promotions } = useFetchHotelPromotions();
  const { promotionAddedValues, promotionType } = promotions || {};
  const showPromotionExtras = !!(promotionAddedValues && promotionAddedValues.length !== 0 && promotionType);
  const { offersData, isLoadingRooms, isSoldout } = useOffersContext();
  const { accommodationOnly } = useUrlSearchParams();
  const { localization } = usePageWrapperData();
  const cheapestFilteredMealPlan = isLoadingRooms ? undefined : offersData?.cheapestFilteredMealPlan;
  const [isModalOpen, setModalOpen] = useState(false);
  const urlSearchParams = useUrlSearchParams();
  const roomsIncludedInPrice = offersData?.rooms?.[0].roomsIncludedInPrice;
  const nights = offersData?.nights || urlSearchParams.nights;
  const passengers = urlSearchParams.totalGuestsWithoutInfants;
  const showDeferredPayments = !!cheapestFilteredMealPlan?.deferredPaymentDeposit;
  const showSplitPayments = isSplitPaymentsEligible({
    localization,
    totalPrice: cheapestFilteredMealPlan?.finalTotalPrice,
    isHO: accommodationOnly,
  });
  const showPackageDetails =
    !urlSearchParams.accommodationOnly &&
    cheapestFilteredMealPlan &&
    (cheapestFilteredMealPlan.backToTravel || showDeferredPayments || showSplitPayments);

  return (
    <PackageDetailsListContainer>
      <Text
        variant="Title5"
        css={`
          display: none;
          @media ${customCss.breakpoints.sm} {
            display: initial;
          }
        `}
      >
        {t(accommodationOnly ? 'vacation.select.details.tripoverview.title' : 'vacation.select.summary.subtitle')}
      </Text>

      <div
        css={`
          display: flex;
          flex-direction: column;
          gap: 2px;
        `}
      >
        <TripDetails />
        {!isSoldout && !accommodationOnly ? (
          cheapestFilteredMealPlan ? (
            <RoomMealPlanInfoContainer>
              <RoomMealPlanAndLuggageInformation mealPlan={cheapestFilteredMealPlan} iconSize={20} />
            </RoomMealPlanInfoContainer>
          ) : (
            <MealPlanDetailsLoading />
          )
        ) : undefined}
      </div>
      {showPromotionExtras && (
        <>
          <Text variant="Title5">{t('vacation.select.summary.lmdeals.extras')}</Text>
          <LmnDealsExtras extras={promotionAddedValues} />
        </>
      )}

      {showPackageDetails ? (
        <>
          <LinkButton
            variant="accent"
            onClick={() => setModalOpen(true)}
            css={css`
              padding: 2px 0;
              z-index: 1;
              white-space: nowrap;
              align-self: flex-start;
              ${({ theme }) => theme.kallisto.mixins.font('BodySmall')};
            `}
          >
            {t('vacation.select.tep.cta_to_open_modal')}
          </LinkButton>
          <PackageDetailsModal
            cheapestMealPlan={cheapestFilteredMealPlan}
            roomsIncludedInPrice={roomsIncludedInPrice ?? 1}
            nights={nights}
            passengers={passengers}
            showSmallAmountDeposit={!!offersData?.showSmallAmountDeferredPayments}
            isOpen={isModalOpen}
            onClose={() => setModalOpen(false)}
            onOpenByBrowserHistory={() => setModalOpen(true)}
          />
        </>
      ) : isLoadingRooms && !urlSearchParams.accommodationOnly ? (
        <Placeholder height="18px" width="80px" />
      ) : undefined}
    </PackageDetailsListContainer>
  );
}

function TripDetails() {
  const { offersData } = useOffersContext();
  const { checkin, checkout, accommodationOnly } = useUrlSearchParams();

  const partialStay =
    (offersData?.checkin && checkin && offersData?.checkin !== checkin) ||
    (offersData?.checkout && checkout && offersData?.checkout !== checkout) ||
    false;

  return accommodationOnly ? <TripDetailsHO /> : partialStay ? <TripDetailsPartialStay /> : <TripDetailsDP />;
}

function TripDetailsHO() {
  const { localization } = usePageWrapperData();
  const { offersData, isLoadingRooms } = useOffersContext();
  const { checkin, checkout } = useUrlSearchParams();
  const cheapestFilteredMealPlan = isLoadingRooms ? undefined : offersData?.cheapestFilteredMealPlan;

  const { checkin: checkinFormatted, checkout: checkoutFormatted } = getCheckinCheckoutDates(
    localization.locale,
    checkin,
    checkout,
    SERVICES_DATES_FORMAT
  );

  return (
    <DetailsInfo>
      <DatesInfo
        checkin={checkinFormatted}
        checkout={checkoutFormatted}
        css={`
          flex-basis: 100%;
          justify-content: space-between;

          @media ${customCss.breakpoints.sm} {
            justify-content: flex-start;
          }
        `}
      />
      <TravellersInfo partialStay={false} />
      {cheapestFilteredMealPlan &&
      hasPaymentInfo({
        deferredPaymentDeposit: cheapestFilteredMealPlan.deferredPaymentDeposit,
        localization,
        totalPrice: cheapestFilteredMealPlan.finalTotalPrice,
        isHO: true,
      }) ? (
        <DetailInfoRow>
          <MealPlanDetailsDeferredPayments mealPlan={cheapestFilteredMealPlan} />
        </DetailInfoRow>
      ) : undefined}
    </DetailsInfo>
  );
}

function TripDetailsDP() {
  const { localization } = usePageWrapperData();
  const { isTrain, checkin, checkout } = useUrlSearchParams();
  const { checkin: checkinFormatted, checkout: checkoutFormatted } = getFlightDates(
    localization.locale,
    checkin,
    checkout
  );

  return (
    <DetailsInfo>
      <DatesInfo
        checkin={checkinFormatted}
        checkout={checkoutFormatted}
        css={`
          flex-basis: 100%;
          justify-content: space-between;

          @media ${customCss.breakpoints.sm} {
            justify-content: flex-start;
          }
        `}
      />
      <TravellersInfo partialStay={false} />
      <DetailInfoRow>
        <Icon
          category="Transportation"
          name={isTrain ? 'icon-train' : 'icon-airplane-right'}
          size={20}
          color="iconPrimary"
          alt="Return flights included"
        />

        <ReturnTransportCityLabel />
      </DetailInfoRow>
    </DetailsInfo>
  );
}

function TripDetailsPartialStay() {
  const { localization } = usePageWrapperData();
  const { offersData } = useOffersContext();
  const { isTrain, checkin, checkout } = useUrlSearchParams();

  const { checkin: tripStart, checkout: tripEnd } = getCheckinCheckoutDates(
    localization.locale,
    checkin,
    checkout,
    SERVICES_DATES_FORMAT
  );

  const { checkin: hotelCheckinDate, checkout: hotelCheckoutDate } =
    offersData?.checkin && offersData?.checkout
      ? getCheckinCheckoutDates(localization.locale, offersData?.checkin, offersData?.checkout, SERVICES_DATES_FORMAT)
      : { checkin: tripStart, checkout: tripEnd };

  return (
    <DetailsInfo>
      <div
        css={`
          display: flex;
          flex-direction: column;
          column-gap: 16px;
          row-gap: 2px;
          flex-basis: 100%;

          @media ${customCss.breakpoints.sm} {
            flex-direction: row;
          }
        `}
      >
        <DatesInfo
          checkin={hotelCheckinDate}
          checkout={hotelCheckoutDate}
          hideDatePicker={true}
          css={`
            ${DatesLabel} {
              font-weight: 700;
            }
          `}
        />
        <TravellersInfo partialStay={true} />
      </div>

      <DetailInfoRow
        css={`
          align-items: flex-start;
        `}
      >
        <Icon
          category="Transportation"
          name={isTrain ? 'icon-train' : 'icon-airplane-right'}
          size={20}
          color="iconPrimary"
          alt="Return flights included"
        />
        <div
          css={`
            display: flex;
            flex-wrap: wrap;
            align-items: flex-start;
            flex-direction: column;

            @media ${customCss.breakpoints.sm} {
              flex-direction: row;
              align-items: center;
              column-gap: 8px;
            }
          `}
        >
          <Text
            variant="BodySmall"
            css={`
              font-weight: bold;
              white-space: nowrap;
            `}
          >
            {`${tripStart} - ${tripEnd}`}
          </Text>
          <Text
            variant="BodySmall"
            css={`
              display: none;
              @media ${customCss.breakpoints.sm} {
                display: block;
              }
            `}
          >
            -
          </Text>

          <ReturnTransportCityLabel />
        </div>
      </DetailInfoRow>
    </DetailsInfo>
  );
}

function DatesInfo(
  props: { checkin: string; checkout: string; hideDatePicker?: boolean } & React.HTMLAttributes<HTMLDivElement>
) {
  const { checkin, checkout, hideDatePicker = false, ...rest } = props;
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  return (
    <DetailInfoRow {...rest}>
      <div
        css={`
          display: flex;
          align-items: center;
          gap: 8px;
        `}
      >
        <Icon
          category="Office and stationery"
          name="icon-calendar-grid"
          size={20}
          color="iconPrimary"
          alt="dates icon"
        />
        {/* // TODO. This is only a temporal patch. There is a SSR hydration error because dates on server and client are render differently:
                      // Server: Wed 12 Feb - Fri 14 Feb,
                      // Client: Wed, 12 Feb - Fri, 14 Feb (does not happen locally) 
                      // */}
        {isMounted ? (
          <DatesLabel>{`${checkin} - ${checkout}`}</DatesLabel>
        ) : (
          <Placeholder height="18px" width="180px" />
        )}
      </div>
      {!hideDatePicker && <InlineDatePicker />}
    </DetailInfoRow>
  );
}

const DatesLabel = styled(Text).attrs({ variant: 'BodySmall' })`
  white-space: nowrap;
`;

export function TravellersInfo(props: { partialStay: boolean }) {
  const { partialStay } = props;
  const { t } = useTranslation();
  const { offersData } = useOffersContext();
  const { nights, totalGuests, accommodationOnly } = useUrlSearchParams();

  return (
    <DetailInfoRow>
      <Icon category="Services" name="icon-double-bed" size={20} color="iconPrimary" alt="Nights of stay" />
      <div
        css={`
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
          & > * {
            white-space: nowrap;
          }
        `}
      >
        <Text variant="BodySmall" css={partialStay ? `font-weight: bold;` : undefined}>
          {nights === 1
            ? t('vacation.select.summary.night')
            : t('vacation.select.summary.nights', {
                amount: offersData?.nights?.toString() || nights.toString(),
              })}
        </Text>
        <Text variant="BodySmall">{`${totalGuests === 1 ? t(accommodationOnly ? 'vacation.select.summary.guest' : 'vacation.select.summary.traveller') : t(accommodationOnly ? 'vacation.select.summary.guests' : 'vacation.select.summary.travellers', { amount: totalGuests.toString() })}`}</Text>
      </div>
    </DetailInfoRow>
  );
}

export function ReturnTransportCityLabel() {
  const { t } = useTranslation();
  const { isTrain } = useUrlSearchParams();
  const { offersData } = useOffersContext();
  const originTransportCity = offersData?.transport?.transports?.[0]?.outbound?.segments[0].departureHub.cityName;

  return (
    <TextBase>
      {originTransportCity
        ? t(isTrain ? 'vacation.select.train.summary.return_train' : 'vacation.select.summary.return_flight', {
            origin: originTransportCity,
          })
        : t(
            isTrain
              ? 'vacation.select.train.summary.return_train.no_city'
              : 'vacation.select.summary.return_flight.no_city'
          )}
    </TextBase>
  );
}
